import { PERMISSIONS, type PermissionCode, type PermissionDefinition } from '@base/shared';
import { WEB_MODULES } from './modules/index.ts';

/**
 * Organização das permissões no assistente de perfis (no estilo do Bankline Itaú): departamento
 * do menu (ADR 0012) → grande processo → funcionalidade (a permissão). Só muda a apresentação:
 * o perfil continua gravando a lista de permissões. Cada permissão aparece em um lugar só; um
 * teste confere que toda permissão nova do catálogo entrou aqui.
 *
 * O nome do departamento vem de `areas.<id>`; o do processo, de `roles.wizard.processes.<id>`;
 * num módulo instalado, da `key` de cada um (`areaNameKey`, `processNameKey`).
 */
export interface PermissionProcess {
  id: string;
  /** Chave do nome; sem ela, `roles.wizard.processes.<id>` (os módulos usam a deles, ADR 0123). */
  key?: string;
  permissions: readonly PermissionCode[];
}

export interface PermissionArea {
  id: string;
  /** Chave do nome; sem ela, `areas.<id>` (os módulos usam a deles, ADR 0123). */
  key?: string;
  processes: readonly PermissionProcess[];
}

const BASE_PERMISSION_AREAS: readonly PermissionArea[] = [
  {
    id: 'settings',
    processes: [
      {
        id: 'access',
        permissions: [
          'iam.user.read',
          'iam.user.manage',
          'iam.access.grant',
          'iam.role.read',
          'iam.role.manage',
        ],
      },
      { id: 'platformControl', permissions: ['audit.log.read', 'audit.access.read'] },
      { id: 'platformLook', permissions: ['platform.settings.manage'] },
      { id: 'platformAi', permissions: ['platform.ai.chat'] },
    ],
  },
];

/** Os da base e os dos módulos instalados (ADR 0123); um departamento repetido junta os processos. */
export const PERMISSION_AREAS: readonly PermissionArea[] = mergeAreas([
  ...WEB_MODULES.flatMap((m) => m.permissionAreas),
  ...BASE_PERMISSION_AREAS,
]);

function mergeAreas(areas: readonly PermissionArea[]): PermissionArea[] {
  const byId = new Map<string, PermissionArea>();
  for (const a of areas) {
    const prev = byId.get(a.id);
    byId.set(a.id, prev ? { id: a.id, processes: [...prev.processes, ...a.processes] } : a);
  }
  return [...byId.values()];
}

/** A chave do nome de um departamento do assistente. */
export function areaNameKey(area: PermissionArea | string): string {
  const found = typeof area === 'string' ? PERMISSION_AREAS.find((a) => a.id === area) : area;
  return found?.key ?? `areas.${typeof area === 'string' ? area : area.id}`;
}

/** A chave do nome de um processo do assistente. */
export function processNameKey(process: PermissionProcess): string {
  return process.key ?? `roles.wizard.processes.${process.id}`;
}

const DEFINITIONS = new Map<string, PermissionDefinition>(PERMISSIONS.map((p) => [p.code, p]));

export function permissionDefinition(code: string): PermissionDefinition | undefined {
  return DEFINITIONS.get(code);
}

/** Todas as permissões de um departamento ou de um processo. */
export function codesOf(item: PermissionArea | PermissionProcess): PermissionCode[] {
  return 'processes' in item ? item.processes.flatMap((p) => p.permissions) : [...item.permissions];
}

/** Departamentos que têm alguma das permissões (para abrir um perfil já gravado). */
export function areasWith(permissions: Iterable<string>): string[] {
  const held = new Set(permissions);
  return PERMISSION_AREAS.filter((a) => codesOf(a).some((c) => held.has(c))).map((a) => a.id);
}

/** Só as permissões que estão nos departamentos escolhidos. */
export function withinAreas(permissions: Iterable<string>, areaIds: Iterable<string>): Set<string> {
  const allowed = new Set(
    PERMISSION_AREAS.filter((a) => new Set(areaIds).has(a.id)).flatMap(codesOf) as string[],
  );
  return new Set([...permissions].filter((c) => allowed.has(c)));
}

/**
 * Tipos de perfil: só modelos que pré-marcam as funcionalidades nos departamentos escolhidos.
 * O tipo não é gravado e não limita nada. Cada módulo novo pode acrescentar os seus modelos.
 */
export const PROFILE_TEMPLATES = ['custom', 'backofficeOperator', 'departmentManager'] as const;
export type ProfileTemplate = (typeof PROFILE_TEMPLATES)[number];

/** Administração do sistema (acessos, auditoria e aparência): nenhum modelo pré-marca. */
const ADMINISTRATION: readonly PermissionCode[] = [
  'iam.user.read',
  'iam.user.manage',
  'iam.access.grant',
  'iam.role.read',
  'iam.role.manage',
  'audit.log.read',
  'audit.access.read',
  'platform.settings.manage',
  'platform.ai.chat',
];

const TEMPLATE_RULES: Record<ProfileTemplate, (p: PermissionDefinition) => boolean> = {
  custom: () => false,
  // Dia a dia: sem as sensíveis, as expressas e as de administração do sistema.
  backofficeOperator: (p) =>
    !p.sensitive && !p.explicitOnly && !ADMINISTRATION.includes(p.code as PermissionCode),
  departmentManager: (p) => !p.explicitOnly && !ADMINISTRATION.includes(p.code as PermissionCode),
};

/** Funcionalidades que o modelo pré-marca nos departamentos escolhidos. */
export function templatePermissions(
  template: ProfileTemplate,
  areaIds: Iterable<string>,
): Set<string> {
  const rule = TEMPLATE_RULES[template];
  const chosen = new Set(areaIds);
  return new Set(
    PERMISSION_AREAS.filter((a) => chosen.has(a.id))
      .flatMap(codesOf)
      .filter((code) => {
        const def = DEFINITIONS.get(code);
        return def !== undefined && rule(def);
      }),
  );
}

/** Código do perfil sugerido a partir do nome: "Operador BackOffice" → OPERADOR-BACKOFFICE. */
export function roleCodeFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/, '');
}
